import type { FC } from 'react';
import { BellRing, ClipboardList } from 'lucide-react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { str, arr } from '@/app/lib/a2ui/props.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { SuggestedReply } from '@/app/lib/a2ui/blocks/SuggestedReply.tsx';
import { MarkdownText } from '@/app/lib/a2ui/blocks/MarkdownText.tsx';

export const ReceptionAnswer: FC<A2uiNodeViewProps> = ({ renderChildren }) => <>{renderChildren()}</>;
export const AppointmentNote: FC<A2uiNodeViewProps> = ({ renderChildren }) => <>{renderChildren()}</>;

export const ReceptionAnswerContent: FC<A2uiNodeViewProps> = ({ node }) => {
  const props = node.props;
  const dispatch = useSurfaceAction();
  const replies = arr(props.replies).filter(isRecord);
  return <section className="reception-answer mx-auto w-full max-w-[720px] py-4 text-foreground sm:py-8">
    <BellRing className="mb-7 h-8 w-8 text-primary" strokeWidth={1.4} aria-hidden="true" />
    {str(props.highlight) && <h1 style={{ fontFamily: "Georgia, 'Times New Roman', serif" }} className="mb-6 text-balance text-4xl font-normal leading-[1.12] tracking-tight sm:text-5xl">{str(props.highlight)}</h1>}
    {str(props.message) && <div className="max-w-[600px] text-lg leading-relaxed"><MarkdownText text={str(props.message)} /></div>}
    {replies.length > 0 && <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-t border-border pt-5">
      {replies.map((reply, index) => <SuggestedReply key={str(reply.id) || index} disabled={!str(reply.id) || !str(reply.label)} onClick={() => dispatch?.('askReception', { intentId: str(reply.id) }, str(reply.label))}>{str(reply.label)}</SuggestedReply>)}
    </div>}
  </section>;
};

export const AppointmentNoteContent: FC<A2uiNodeViewProps> = ({ node }) => {
  const props = node.props;
  const details = arr(props.details).filter(isRecord);
  return <section className="reception-answer mx-auto w-full max-w-[720px] rounded-2xl border border-border bg-card p-6 text-card-foreground sm:p-9">
    <div className="mb-4 flex items-center gap-3 text-primary"><ClipboardList className="h-6 w-6 shrink-0" aria-hidden="true" /><p className="text-base font-semibold">{str(props.testLabel)}</p></div>
    <p className="mb-7 border-s-2 border-warning ps-4 text-base leading-relaxed">{str(props.notice)}</p>
    {str(props.highlight) && <h2 style={{ fontFamily: "Georgia, 'Times New Roman', serif" }} className="mb-5 text-balance text-3xl font-normal">{str(props.highlight)}</h2>}
    <dl className="divide-y divide-border">
      {details.map((detail, index) => <div key={str(detail.id) || index} className="flex flex-wrap gap-x-6 gap-y-1 py-4">
        <dt className="w-40 shrink-0 text-base text-muted-foreground">{str(detail.label)}</dt>
        <dd className="min-w-0 flex-1 basis-48 break-words text-base font-medium">{str(detail.value)}</dd>
      </div>)}
    </dl>
    {str(props.message) && <div className="mt-6 max-w-[600px] text-base leading-relaxed"><MarkdownText text={str(props.message)} /></div>}
  </section>;
};
